/**
 * Travel Page - Separate route for travel information
 * 
 * Accessed via:
 * - Explore section entry point
 * - Hamburger menu navigation
 * 
 * GATED: Only accessible when PHASE_2_RELEASE=true
 * Structure only - no integrations yet
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plane, Car, MapPin, Train } from 'lucide-react';
import TOKENS from '../data/tokens';
import PreviewBanner from '../components/PreviewBanner';
import MaintenanceScreen from '../components/MaintenanceScreen';
import { isMaintenanceMode } from '../utils/featureGates';

const TravelPage = () => {
  const navigate = useNavigate();
  
  // MAINTENANCE MODE CHECK
  if (isMaintenanceMode()) {
    return <MaintenanceScreen />;
  }
  
  // Check if preview banner will be shown (to add padding)
  const isPreview = window.location.hostname.includes('preview.emergentagent.com') ||
                    window.location.hostname.includes('localhost') ||
                    window.location.hostname.includes('127.0.0.1');
  
  return (
    <div style={{ 
      minHeight: '100vh',
      background: TOKENS.colors.heroBackground,
      paddingTop: isPreview ? 48 : 0, // Account for preview banner height
    }}>
      <PreviewBanner />
      
      {/* Simple header with back button */}
      <div style={{
        padding: '16px 24px',
        borderBottom: `1px solid ${TOKENS.colors.borderGrey}`,
        background: TOKENS.colors.cardBackground,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}>
        <button
          onClick={() => navigate('/', { state: { bypassSplash: true } })}
          style={{
            background: 'rgba(212, 168, 83, 0.1)',
            border: '1px solid rgba(212, 168, 83, 0.3)',
            borderRadius: 24,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 18px',
            color: TOKENS.colors.textGold,
            fontSize: 14,
            fontWeight: 600,
            transition: 'all 0.2s ease',
          }}
          data-testid="travel-back-btn"
        >
          <ArrowLeft size={18} />
          <span>Return</span>
        </button>
      </div>
      
      {/* Page Content */}
      <div style={{ padding: '40px 24px', maxWidth: 600, margin: '0 auto' }}>
        {/* Page Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 64,
            height: 64,
            background: '#FFF3E0',
            border: `1px solid ${TOKENS.colors.borderGrey}`,
            borderRadius: TOKENS.borderRadius.xl,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 16px',
          }}>
            <Plane size={28} color={TOKENS.colors.textGold} />
          </div>
          
          <div style={{ 
            fontSize: TOKENS.fontSizes.labelSmall, 
            fontWeight: TOKENS.fontWeights.bold,
            color: TOKENS.colors.textGoldDark, 
            letterSpacing: TOKENS.letterSpacing.wide, 
            marginBottom: 8,
            textTransform: 'uppercase'
          }}>
            GETTING HERE
          </div>
          <h1 style={{ 
            fontFamily: TOKENS.fonts.heading, 
            fontSize: 28, 
            fontWeight: TOKENS.fontWeights.medium, 
            color: TOKENS.colors.textBlack, 
            margin: 0,
          }}>
            Travel Information
          </h1>
        </div>
        
        {/* Travel Cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <TravelCard
            icon={Plane}
            title="By Air"
            content="We recommend flying into the nearest major airport. Shuttle arrangements and transportation details will be shared closer to the event date."
          />
          <TravelCard
            icon={Car}
            title="By Car"
            content="Driving directions and parking accommodations will be provided in your event packet. Ample parking will be available."
          />
          <TravelCard
            icon={Train}
            title="By Train"
            content="For those traveling by rail, we'll share the nearest station and local transit connections in your event details."
          />
          <TravelCard
            icon={MapPin}
            title="Venue Location"
            content="The celebration venue will be announced soon. You'll receive the full address and directions with your invitation."
          />
        </div>
        
        {/* Notice */}
        <div style={{
          textAlign: 'center',
          marginTop: 32,
          padding: '20px 24px',
          background: 'linear-gradient(135deg, rgba(212,168,83,0.08) 0%, rgba(232,164,184,0.04) 100%)',
          borderRadius: 12,
          border: '1px solid rgba(212,168,83,0.15)',
        }}>
          <div style={{
            fontSize: 14,
            color: TOKENS.colors.textGrey,
            lineHeight: 1.6,
          }}>
            Full travel details will be included in your invitation package.
          </div>
        </div>
      </div>
    </div>
  );
};

const TravelCard = ({ icon: Icon, title, content }) => (
  <div style={{
    background: 'linear-gradient(135deg, rgba(212,168,83,0.6), rgba(198,167,90,0.25), rgba(212,168,83,0.6))',
    padding: 2,
    borderRadius: 14,
  }}>
    <div style={{ 
      background: TOKENS.colors.cardBackground, 
      borderRadius: 12, 
      padding: 20,
      display: 'flex',
      alignItems: 'flex-start',
      gap: 16,
    }}>
      <div style={{
        width: 44,
        height: 44,
        background: 'linear-gradient(135deg, rgba(212,168,83,0.12) 0%, rgba(232,164,184,0.08) 100%)',
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon size={22} color={TOKENS.colors.textGold} />
      </div>
      <div>
        <div style={{ 
          fontSize: 16, 
          fontWeight: TOKENS.fontWeights.semiBold, 
          color: TOKENS.colors.textBlack,
          marginBottom: 6,
        }}>
          {title}
        </div>
        <div style={{ 
          fontSize: 14, 
          color: TOKENS.colors.textGrey, 
          lineHeight: 1.5,
        }}>
          {content}
        </div>
      </div>
    </div>
  </div>
);

export default TravelPage;
